import React from 'react';
import { TrackingHelper, AddToWishlistTrackingData } from '@escid-espp1/helpers/tracking';
import {
    ArticleData,
    BookMarkArticleDataInjectionRequest,
    BookmarkClickPayload,
    IBookmarkArticleDataResponse,
    IBookmarkModalProps,
    BookmarkRequestsLoginData
} from '../types';
import styles from './bookmarkModal.scss';
import BookmarkIcon from '../icons/bookmark_icon';
import { AddResponse, BackendAjaxAdapter } from '../bookmark-button/BackendAjaxAdapter';
import Cross from '../icons/cross';
import { EsAppHelper } from '../../helpers/esAppHelper';
import { ScrollLock } from '../../helpers/scrollLock';
import { setFocusableElement, setFocusableGroup } from '../../helpers/domHelpers';
import { mapArticleData, mapBookmarkTrackingData } from '../../helpers/bookmarkMappers';
import '../../styles/customStyles.scss';

function removeBookmarkParametersFromUrl() {
    if (typeof window === 'undefined' || !window.history) {
        return;
    }

    const url = new URL(window.location.href);
    const paramsToRemove = ['addBookmark', 'bm'];

    for (const param of paramsToRemove) {
        url.searchParams.delete(param);
    }

    window.history.replaceState(null, '', url.toString());
}

function b64EncodeUnicode(str: string): string {
    const PERCENT_ENCODED_BYTE = /%([0-9A-F]{2})/g;

    return btoa(
        encodeURIComponent(str).replace(
            PERCENT_ENCODED_BYTE,
            (_match, hex) => String.fromCharCode(parseInt(hex, 16))
        )
    );
}

function closeOrderModal() {
    if (window.shell) {
        window.shell.publishTo('ESPP.OrderModal.Close', null);
    }
}

const backend: BackendAjaxAdapter = new BackendAjaxAdapter();
const esAppHelper: EsAppHelper = new EsAppHelper();
const scrollLock: ScrollLock = ScrollLock.instance;

function BookmarkModalSingleton(props: IBookmarkModalProps) {
    const [openState, setOpenState] = React.useState(props.isOpen);
    const [articleDataState, setArticleDataState] = React.useState<ArticleData>(props.articleDataAfterLogin);
    const [previouslySelectedElement, setPreviouslySelectedElement] = React.useState<Element | null>(null);
    const bookmarkedItemsBtnRef = React.useRef<HTMLDivElement | null>(null);
    const bookmarkModalRef = React.useRef<HTMLDivElement | null>(null);

    const trackingHelper: TrackingHelper = React.useMemo<TrackingHelper>(() => {
        return new TrackingHelper(
            false,
            false,
            props.enableTikTokPixel,
            props.enableKraken,
            props.enableClientLogging,
            null!,
            null
        );
    }, [props.enableTikTokPixel, props.enableKraken, props.enableClientLogging]);

    const { localization } = props;

    React.useEffect(() => {
        if (typeof window !== 'undefined' && window.shell) {
            // react to special URL parameter that triggers bookmark after login (logged out user clicked bookmark)
            const isAfterLoginCase = !!props.articleDataAfterLogin;
            if (isAfterLoginCase) {
                // we already added the bookmark server-side 
                openBookmarkModal(props.articleDataAfterLogin);

                //if modal is already opened serverside, we still need to trim the URL
                removeBookmarkParametersFromUrl();

                if (props.articleTrackingData) {
                    const trackingData: AddToWishlistTrackingData = {
                        id: props.articleDataAfterLogin.savKey,
                        name: props.articleDataAfterLogin.productName,
                        category: props.articleTrackingData.navKeyPath,
                        currencyCode: props.articleTrackingData.currencyISOCode,
                        variantId: null,
                        brand: null,
                        price: { netValue: null, grossValue: null },
                        grossPriceString: Number(props.articleTrackingData.grossPrice).toFixed(2),
                    }

                    if (!props.disableDirectEcommerceTracking) {
                        trackingHelper.tiktokTrackAddArticleToWishlist(trackingData);
                    }
                    
                    if (props.enableKraken) {
                        trackingData.id = props.articleDataAfterLogin.savKey?.split('-')[0];
                        trackingData.variantId = props.articleDataAfterLogin.savKey;
                        trackingHelper.krakenTrackAddToWishlist(trackingData, 'ESPP3.Bookmark');
                    }
                }
            }

            window.shell.subscribeTo('ESCID.ESPP.Bookmark.ButtonWasClicked',
                (payload: BookmarkClickPayload) => {
                    handleBookmarkClick(payload);
                },
                'ESCID.ESPP.Bookmark');
        }
    }, []);

        /*
    * Reacts to button click or after-login-bookmark
    * Gets data -> calls backend -> opens modal
    */
    const handleBookmarkClick = (payload: BookmarkClickPayload) => {
        const articleDataRequest: BookMarkArticleDataInjectionRequest = {
            requester: payload.buttonProps.containerId,
            origin: payload.buttonProps.origin
        };

        if (props.isLoggedIn) {
            handleAuthorizedBookmarkClick(payload, articleDataRequest);
            return;
        }

        handleUnauthorizedBookmarkClick(articleDataRequest);
    }

    const handleAuthorizedBookmarkClick = (
        payload: BookmarkClickPayload,
        articleDataRequest: BookMarkArticleDataInjectionRequest
    ) => {
        const handleArticleDataInjection = (articleDataResponse: IBookmarkArticleDataResponse) => {
            if (articleDataResponse.requester !== payload.buttonProps.containerId) {
                return;
            }

            const articleData = mapArticleData(articleDataResponse);
            const bookmarkTrackingData = mapBookmarkTrackingData(articleDataResponse);

            backend.addBookmark(articleData, props.magicLink).then((addResponse: AddResponse) => {
                if (!addResponse.added) {
                    return;
                }

                openBookmarkModal(articleData, payload.focusMethod);

                const trackingData: AddToWishlistTrackingData = {
                    id: articleData.savKey,
                    name: articleData.productName,
                    category: bookmarkTrackingData.navKeyPath,
                    price: { netValue: null, grossValue: Number(bookmarkTrackingData.grossPrice) },
                    currencyCode: bookmarkTrackingData.currencyISOCode,
                    variantId: null,
                    brand: null,
                    grossPriceString: Number(bookmarkTrackingData.grossPrice).toFixed(2),
                }

                if (!props.disableDirectEcommerceTracking) {
                    trackingHelper.tiktokTrackAddArticleToWishlist(trackingData);
                }

                if (props.enableKraken) {
                    trackingData.id = articleData.savKey?.split('-')[0];
                    trackingData.variantId = articleData.savKey;
                    trackingHelper.krakenTrackAddToWishlist(trackingData, 'ESPP3.Bookmark');
                }
            });
        }

        window.shell.subscribeTo(
            'ESCID.ESPP.Bookmark.ArticleDataInjection',
            handleArticleDataInjection,
            'ESCID.ESPP.Bookmark.ArticleDataInjection');

        window.shell.publishTo('ESCID.ESPP.Bookmark.RequestArticleDataInjection', articleDataRequest);
    }

    const handleUnauthorizedBookmarkClick = (
        articleDataRequest: BookMarkArticleDataInjectionRequest
    ) => {
        window.shell.once('ESCRM.AccountMenu.AccountMenuSwitchedToLoginRequestedMode', () => {
            //this opens the flyout
            window.shell.publishTo('ESMKT.HeadR.ToggleFlyout', 'LoginFlyout');
            //close also legacy order modal
            window.shell.publishTo('mpc.close-modal', null);
            //close order modal
            window.shell.publishTo('ESPP.OrderModal.Close', null);
        });

        const handleArticleDataInjection = (
            articleData: IBookmarkArticleDataResponse
        ) => {
            const currentPage: string = window.location.pathname + window.location.search;
            // type safe to avoid weird behavior on backend parsing
            // for string "59,38" parse to float on backend can be 5938. So always provide correct type here
            const grossPriceString = typeof articleData.grossPrice === 'number' ?
                articleData.grossPrice.toFixed(2).toString() :
                articleData.grossPrice;
            articleData.grossPrice = grossPriceString;

            const objJsonStr = JSON.stringify(articleData);
            const objJsonB64 = b64EncodeUnicode(objJsonStr);
            const urlEncodedBase64 = encodeURIComponent(objJsonB64);
            const urlWithParam = currentPage + (currentPage.indexOf('?') >= 0 ? '&' : '?')
                + 'addBookmark=true'
                + '&bm=' + urlEncodedBase64;

            if (props.isEsApp) {
                //esApp does not use AMS, but instead redirects to Login Page 
                // we created our own login page that contains a special message that "bookmark requires login"
                const urlWithParamEncoded = encodeURIComponent(urlWithParam)

                const param = `?loginRequired=true&redirectUrl=${urlWithParamEncoded}`;
                const loginForEsAppWithRedirect = `${props.localization.esAppLoginPageUrl}${param}`;

                window.location.href = loginForEsAppWithRedirect;
                return;
            }

            const loginRequestData: BookmarkRequestsLoginData = {
                redirectUrl: urlWithParam,
                message: props.localization.loginHint
            };

            //we created our own AMS login flyout design
            //that contains a special message that "bookmark requires login"
            //this switches the flyout design
            window.shell.publishTo('ESCRM.AccountMenu.ServiceRequestsLogin', loginRequestData);
            window.shell.publishTo('legacy.modals.close', null);
        }

        window.shell.subscribeTo(
            'ESCID.ESPP.Bookmark.ArticleDataInjection',
            handleArticleDataInjection,
            'ESCID.ESPP.Bookmark.ArticleDataInjection'
        );

        window.shell.publishTo('ESCID.ESPP.Bookmark.RequestArticleDataInjection', articleDataRequest);
    }

    const openBookmarkModal = (articleDataFromEvent: ArticleData, focusMethod?: string) => {
        if (!articleDataFromEvent) {
            return;
        }

        scrollLock.lock();
        setArticleDataState(articleDataFromEvent);
        setOpenState(true);
        closeOrderModal();
        esAppHelper.navigationWheel.hide();

        setPreviouslySelectedElement(document.activeElement)

        if (bookmarkedItemsBtnRef.current) {
            setFocusableElement(bookmarkedItemsBtnRef.current, '2');

            setTimeout(() => {
                if (bookmarkedItemsBtnRef.current) {
                    window.shell.tabNav.focus(bookmarkedItemsBtnRef.current, focusMethod);
                }
            }, 300);
        }
    }

    const closeBookmarkModalFb = function closeBookmarkModal() {
        scrollLock.unlock();
        esAppHelper.navigationWheel.show();
        setOpenState(false);
        window.shell.publishTo('ESCID.ESPP.Bookmark.RequestQuickOrderModalClose', null);

        const elementExists = document.contains(previouslySelectedElement);

        if (elementExists) {
            window.shell.tabNav.focus(previouslySelectedElement!);
        }
        else {
            const searchbox = document.querySelector('.searchbox [focusable=""]');
            if (searchbox) {
                window.shell.tabNav.focus(searchbox);
            }
        }
    }

    function goToShortlistPage(event: React.MouseEvent<HTMLElement>) {
        event.preventDefault();
        if (props.isEsApp) {
            esAppHelper.navigationWheel.show();
            // on esApp we have to show the wheel and trigger the navigation
            setTimeout(function () {
                window.location.href = localization.shortlistPageUrl;
            }, 300);
        } else {
            /*
             * For now, it is assumed that the used is logged in 
             * (otherwise it would not be possible to see Bookmark modal)
             * after we do the real check we will actually add an item to customer's list, for now we only have redirect
            */
            window.location.href = localization.shortlistPageUrl;
        }
    }

    const getAltTagValue = (): string => {
        const { categoryName, productName, colorName } = articleDataState;
        const parts: string[] = [props.localization.mainView];

        if (categoryName) {
            parts.push(categoryName);
        }

        parts.push(productName);

        if (colorName) {
            parts.push(colorName);
        }

        return parts.join(', ');
    }

    function handleKeyDown(event, orderNumber: number): void {
        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();

            switch (orderNumber) {
                case 1:
                case 3:
                    closeBookmarkModalFb();
                    break;
                case 2:
                    goToShortlistPage(event);
                    break;
            }
        }

        if (!window.shell.tabNav) {
            return;
        }

        if (event.key === 'ArrowRight') {
            const newOrderNumber = (orderNumber == 3) ? 1 : (orderNumber + 1);
            const ArrowRightElement = document.getElementById(newOrderNumber.toString())
            if (ArrowRightElement) {
                window.shell.tabNav.focus(ArrowRightElement);
            }
        }

        if (event.key === 'ArrowLeft') {
            const newOrderNumber = (orderNumber == 1) ? 3 : (orderNumber - 1);
            const ArrowLeftElement = document.getElementById(newOrderNumber.toString())
            if (ArrowLeftElement) {
                window.shell.tabNav.focus(ArrowLeftElement);
            }
        }
    }

    if (!openState) {
        return <></>;
    }

    const assetReleaseMode = props.isRelease ? 'images' : 'images_preview';
    const imageUrl = `${props.cdnBaseUrl}assets/bookmark/${assetReleaseMode}/BookmarkModalThumbnail/${articleDataState.imageUrl}`;

    return (
        <div
            className={styles.bookmark_modal}
            data-testid="bookmark_modal_root" data-semantic-id="bookmark_modal_root">
            <div
                ref={bookmarkModalRef}
                className={styles.bookmark_modal_container}
                data-testid="bookmark-container" data-semantic-id="bookmark-container"
            >
                <div className={styles.bookmark_modal_content} ref={(el) => setFocusableGroup(el)}>
                    <h2 className={styles.bookmark_modal_title}>
                        <span className={styles.bookmark_modal_title_text}>{localization.modalTitle}</span>
                        <div id="1"
                            ref={(el) => setFocusableElement(el, '1')}
                            onKeyDown={e => handleKeyDown(e, 1)}
                            className={styles.close_button}
                            onClick={closeBookmarkModalFb}
                            data-testid="bookmark_close_button"
                            data-semantic-id="bookmark_close_button">
                            <Cross />
                        </div>
                    </h2>
                    <div className={styles.bookmark_modal_middle}>
                        <div className={styles.image_container}>
                            <img
                                data-testid='bookmark_image' data-semantic-id='bookmark_image'
                                alt={getAltTagValue()}
                                className={styles.bookmark_image}
                                src={imageUrl} />
                        </div>
                        <div className={styles.bookmark_modal_text_box}>
                            <p className={styles.bookmark_modal_text_bold}>
                                {localization.hasBeenAdded}
                            </p>
                            <p className={styles.bookmark_modal_text_bold}>
                                {articleDataState.productName}
                            </p>
                            { articleDataState.colorName &&
                                <p className={styles.bookmark_modal_text_regular}>
                                    <span data-label="color" className={styles.color_capital_letter}>
                                        {localization.color}{localization.colon}&nbsp;</span>
                                    {articleDataState.colorName}
                                </p>
                            }
                            { articleDataState.sizeName &&
                                <p className={styles.bookmark_modal_text_regular}>
                                    <span data-label="size">{localization.size}{localization.colon}&nbsp;</span>
                                    {articleDataState.sizeName}
                                </p>
                            }
                            { articleDataState.modelName &&
                                <p className={styles.bookmark_modal_text_regular}>
                                    <span data-label="model">{localization.model}{localization.colon}&nbsp;</span>
                                    {articleDataState.modelName}
                                </p>
                            }
                        </div>
                    </div>
                    <div className={styles.bookmark_modal_footer}>
                        <div id="2"
                            ref={bookmarkedItemsBtnRef}
                            onKeyDown={e => handleKeyDown(e, 2)}
                            data-testid="to_bookmarked_items_button"
                            data-semantic-id="to_bookmarked_items_button"
                            onClick={e => goToShortlistPage(e)}
                            className={styles.to_bookmarked_items_button}>
                            <BookmarkIcon />
                            <span>{localization.goToBookmarkPage}</span>
                        </div>
                        <div id="3"
                            ref={(el) => setFocusableElement(el, '3')}
                            onKeyDown={e => handleKeyDown(e, 3)}
                            onClick={closeBookmarkModalFb}
                            data-testid="continue_shopping_button"
                            data-semantic-id="continue_shopping_button"
                            className={styles.continue_shopping_button}>
                            {localization.continueShopping}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BookmarkModalSingleton;
